import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import { usePermissions } from '@/features/permissions/hooks/use-permissions';
import { useMembers, type MemberOut } from '../api/members-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { signingError } from '@/features/emr/components/ReportSigningPanel';
type Eligibility = { user_id: string; enabled: boolean; credentials: string };
export function SigningEligibilityCard() {
  const { organization } = useAuth();
  const { can } = usePermissions();
  const { data: members = [] } = useMembers();
  const base = `/api/v1/organizations/${organization?.organization_id}/signing-eligibility`;
  const allowed = can('manage_signing_eligibility');
  const { data: rows = [], refetch } = useQuery<Eligibility[]>({
    queryKey: ['signing-eligibility', organization?.organization_id],
    enabled: allowed && !!organization,
    queryFn: async () => (await clientApi.get(base)).data,
  });
  if (!allowed) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Clinical signing eligibility</CardTitle>
        <CardDescription>
          Authorize clinical signers individually. Their role must also include Sign document and Amend document
          permissions. Administrative access alone never permits signing.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {members.map((member) => {
          const eligibility = rows.find((row) => row.user_id === member.user_id);
          return (
            <EligibilityRow
              key={`${member.id}-${eligibility?.enabled}-${eligibility?.credentials}`}
              member={member}
              eligibility={eligibility}
              base={base}
              refresh={() => {
                void refetch();
              }}
            />
          );
        })}
      </CardContent>
    </Card>
  );
}
function EligibilityRow({
  member,
  eligibility,
  base,
  refresh,
}: {
  member: MemberOut;
  eligibility?: Eligibility;
  base: string;
  refresh: () => void;
}) {
  const [credentials, setCredentials] = useState(eligibility?.credentials || '');
  const [busy, setBusy] = useState(false);
  async function save(enabled: boolean) {
    setBusy(true);
    try {
      await clientApi.put(`${base}/${member.user_id}`, {
        enabled,
        credentials: credentials.trim() || eligibility?.credentials,
      });
      refresh();
      toast.success('Signing eligibility updated');
    } catch (e) {
      toast.error(signingError(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border p-3">
      <Field className="min-w-48 flex-1">
        <FieldLabel htmlFor={`credentials-${member.id}`}>
          {member.user_name || member.user_email} · {eligibility?.enabled ? 'Eligible' : 'Not eligible'}
        </FieldLabel>
        <Input
          id={`credentials-${member.id}`}
          placeholder="Clinical credentials, e.g. MD"
          value={credentials}
          maxLength={200}
          onChange={(e) => setCredentials(e.target.value)}
        />
      </Field>
      <Button disabled={busy || !credentials.trim() || !member.is_active} onClick={() => save(true)}>
        {eligibility?.enabled ? 'Update credentials' : 'Authorize signer'}
      </Button>
      {eligibility?.enabled && (
        <Button variant="outline" disabled={busy} onClick={() => save(false)}>
          Revoke signing
        </Button>
      )}
    </div>
  );
}
